import { Button } from "components/ui/button";
import { DataTableColumnHeader } from "components/ui/data-table-column-header";
import { createStandardHeader } from "components/ui/standard-column-header";
import { titleCase, upperCase } from "lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { useState } from "react";
import * as HeroIcons from "@heroicons/react/24/outline";
import { CnDocumentModal } from "./CnDocumentModel";
import { ShipmentItem, ShipmentStatus } from "types/shipment";

const CnNumberCell: React.FC<{ cnNumber: string }> = ({ cnNumber }) => {
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);

    return (
        <div className="flex items-center space-x-2 w-[120px]">
            <span className="font-medium text-xs">{cnNumber}</span>
            <HeroIcons.DocumentTextIcon
                className="h-4 w-4 text-gray-500 cursor-pointer hover:text-gray-800"
                onClick={() => setIsDocModalOpen(true)}
            />
            <CnDocumentModal
                isOpen={isDocModalOpen}
                onClose={() => setIsDocModalOpen(false)}
                cnNumber={cnNumber}
            />
        </div>
    );
};

const DocumentsCell: React.FC<{ shipment: ShipmentItem; showInvoice?: boolean }> = ({
    shipment,
    showInvoice = true,
}) => {
    const openLink = (url?: string | null) => {
        if (url) window.open(url, "_blank", "noopener,noreferrer");
    };

    return (
        <div className="flex items-center space-x-1">
            <Button
                variant="outline"
                size="sm"
                className={`h-7 px-2.5 py-1 text-xs flex items-center gap-1 ${shipment.pod_link
                    ? "bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
                    : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                    }`}
                onClick={() => openLink(shipment.pod_link)}
                disabled={!shipment.pod_link}
                title={shipment.pod_link ? "Open POD" : "No POD available"}
            >
                <HeroIcons.DocumentCheckIcon className="h-4 w-4" />
                <span>POD</span>
            </Button>

            {showInvoice && (
                <Button
                    variant="outline"
                    size="sm"
                    className={`h-7 px-2.5 py-1 text-xs flex items-center gap-1 ${shipment.invoice_link
                            ? "bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
                            : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                        }`}
                    onClick={() => openLink(shipment.invoice_link)}
                    disabled={!shipment.invoice_link}
                    title={shipment.invoice_link ? "Open Invoice" : "No Invoice available"}
                >
                    <HeroIcons.DocumentTextIcon className="h-4 w-4" />
                    <span>Invoice</span>
                </Button>
            )}
        </div>
    );
};

const ActionsCell: React.FC<{ shipment: ShipmentItem; onRefresh?: () => void; onRefreshAlerts?: () => void; }> = ({ shipment, onRefresh, onRefreshAlerts }) => {
    return (
        <div className="flex items-center space-x-1">
            {
                shipment.status !== "local" && shipment.status !== "delivered" &&
                (
                    <Button
                        variant="outline"
                        size="sm"
                        className="h-7 px-2.5 py-1 text-xs bg-blue-100 text-blue-600 border-blue-200 hover:bg-blue-200 hover:text-blue-700"
                        onClick={() => window.open(`/shipments/${shipment.cn_number}/track`, "_blank")}
                    >
                        <HeroIcons.TruckIcon className="h-4 w-4" />
                        <span className="pl-1">Track</span>
                    </Button>
                )
            }

            {shipment.status === "delivered" && (
                <>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!shipment.pod_link}
                        className={`h-7 px-2.5 py-1 text-xs border ${shipment.pod_link ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200" : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"}`}
                        onClick={() => {
                            if (shipment.pod_link) {
                                window.open(shipment.pod_link, "_blank");
                            }
                        }}
                        title="POD"
                    >
                        <HeroIcons.ClipboardDocumentCheckIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!shipment.invoice_link}
                        className={`h-7 px-2.5 py-1 text-xs border ${shipment.invoice_link ? "bg-green-100 text-green-700 border-green-200 hover:bg-green-200" : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"}`}
                        onClick={() => {
                            if (shipment.invoice_link) {
                                window.open(shipment.invoice_link, "_blank");
                            }
                        }}
                        title="Invoice"
                    >
                        <HeroIcons.DocumentTextIcon className="h-4 w-4" />
                    </Button>
                </>
            )}
        </div>
    );
};

export const cpShipmentColumns = (status: ShipmentStatus | "all", onRefresh?: () => void, onRefreshAlerts?: () => void): ColumnDef<ShipmentItem>[] => {
    const baseColumns: ColumnDef<ShipmentItem>[] = [
        {
            accessorKey: "cn_number",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="CN Number" />
            ),
            cell: ({ row }) => <CnNumberCell cnNumber={row.getValue("cn_number")} />,
            enableSorting: true,
            enableHiding: false,
        },
        {
            accessorKey: "cn_date",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="CN Date" />
            ),
            cell: ({ row }) => {
                const date = new Date(row.getValue("cn_date"));
                return (
                    <div className="w-[100px] text-xs">
                        {date.toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                        })}
                    </div>
                );
            },
            enableSorting: true,
        },
        {
            accessorKey: "consignor",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Consignor" />
            ),
            cell: ({ row }) => {
                const value = row.getValue("consignor") as string;
                return (
                    <div className="max-w-[150px] truncate text-xs" title={value}>
                        {upperCase(value)}
                    </div>
                );
            },
        },
        {
            accessorKey: "consignee",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Consignee" />
            ),
            cell: ({ row }) => {
                const value = row.getValue("consignee") as string;
                return (
                    <div className="max-w-[150px] truncate text-xs" title={value}>
                        {upperCase(value)}
                    </div>
                );
            },
        },
        {
            accessorKey: "origin",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Origin" />
            ),
            cell: ({ row }) => {
                const value = row.getValue("origin") as string;
                return (
                    <div className="max-w-[120px] truncate text-xs" title={value}>
                        {titleCase(value)}
                    </div>
                );
            },
        },
        {
            accessorKey: "destination",
            header: ({ column }) => (
                <DataTableColumnHeader column={column} title="Destination" />
            ),
            cell: ({ row }) => {
                const value = row.getValue("destination") as string;
                return (
                    <div className="max-w-[120px] truncate text-xs" title={value}>
                        {titleCase(value)}
                    </div>
                );
            },
        },
    ];

    const cnDistancekmColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "cn_distance_km",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="CN Distance" />
        ),
        cell: ({ row }) => {
            const totalDistance = row.getValue("cn_distance_km");
            return (
                <div className="w-[70px] text-xs">
                    {totalDistance ? `${totalDistance} km` : "-"}
                </div>
            );
        },
        enableSorting: true,
    }

    const eddColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "edd",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="EDD" />
        ),
        cell: ({ row }) => {
            const edd = row.getValue("edd") as string;
            const isOnHold = edd === "on hold";
            const isAwaitingPickup = edd === "awaiting pickup";
            if (isOnHold) {
                return (
                    <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-orange-100 text-orange-600 border border-orange-200">
                        On Hold
                    </div>
                );
            }
            if (isAwaitingPickup) {
                return (
                    <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-orange-100 text-orange-600 border border-orange-200">
                        Awaiting Pickup
                    </div>
                );
            }

            const date = new Date(row.getValue("edd"));
            return (
                <div
                    className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-green-100 text-green-700 border border-green-200`}
                >
                    {date.toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    })}
                </div>
            );
        },
    };

    const stockStatusColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "stock_status",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Status" />
        ),
        cell: ({ row }) => {
            const value = (row.getValue("stock_status") as string) || "";
            return (
                <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-orange-100 text-orange-600 border border-orange-200">
                    {titleCase(value)}
                </div>
            );
        },
        enableSorting: false,
    };

    const stockLocationColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "stock_location",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Location" />
        ),
        cell: ({ row }) => {
            const value = row.getValue("stock_location") as string;
            return (
                <div className="text-xs max-w-[120px] truncate" title={value}>
                    {titleCase(value)}
                </div>
            );
        },
    };

    const stockSinceColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "stock_since",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Since" />
        ),
        cell: ({ row }) => {
            const date = row.getValue("stock_since");
            if (!date) return "-";

            return (
                <div className="text-xs">
                    {new Date(date as string).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    })}
                </div>
            );
        },
    };

    const stockDaysColumn: ColumnDef<ShipmentItem> = {
        id: "stock_days",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Stock Days" />
        ),
        cell: ({ row }) => {
            const since = row.getValue("stock_since");
            if (!since) return "-";

            const sinceDate = new Date(since as string);
            const today = new Date();
            const diff = Math.floor(
            (today.getTime() - sinceDate.getTime()) / (1000 * 60 * 60 * 24)
            );
            return (
                <div className="text-xs font-medium">{diff} days</div>
            );
        },
    };

    const remainingColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "remaining_km",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Remaining" />
        ),
        cell: ({ row }) => (
            <div className="w-[60px] text-xs">
                {row.getValue("remaining_km") !== null
                    ? `${row.getValue("remaining_km")} km`
                    : "-"}
            </div>
        ),
    };

    const deliveryDateColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "delivery_date",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Delivery Date" />
        ),
        cell: ({ row }) => {
            const date = row.getValue("delivery_date");
            if (!date) return "-";

            return (
                <div className="inline-flex items-center rounded-md px-2.5 py-1 text-xs bg-green-100 text-green-700 border border-green-200">
                    {new Date(date as string).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                    })}
                </div>
            );
        },
        enableSorting: false,
    };

    const loadTypeColumn: ColumnDef<ShipmentItem> = {
        accessorKey: "load_type",
        header: ({ column }) => (
            <DataTableColumnHeader column={column} title="Load Type" />
        ),
        cell: ({ row }) => {
            const value = (row.getValue("load_type") as string) || "";

            // Normalize for styling
            const type = value.toLowerCase();
            let styles = "bg-slate-100 text-slate-700 border-slate-200"; // Default

            if (type.includes("ftl")) {
                styles = "bg-indigo-100 text-indigo-700 border-indigo-200";
            } else if (type.includes("part") || type.includes("ptl")) {
                styles = "bg-orange-100 text-orange-700 border-orange-200";
            } else if (type.includes("odc")) {
                styles = "bg-rose-100 text-rose-700 border-rose-200";
            }

            return (
                <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${styles}`}>
                    {value === "P- Part Load" ? "PTL" : upperCase(value)}
                </div>
            );
        },
        enableSorting: false,
    };

    const actionsColumn: ColumnDef<ShipmentItem> = {
        id: "actions",
        header: createStandardHeader("Actions"),
        cell: ({ row }) => (
            <ActionsCell
                shipment={row.original}
                onRefresh={onRefresh}
                onRefreshAlerts={onRefreshAlerts}
            />
        ),
        enableSorting: false,
        enableHiding: false,
    };

    const documentsColumn: ColumnDef<ShipmentItem> = {
        id: "documents",
        header: createStandardHeader("Documents"),
        cell: ({ row }) => <DocumentsCell shipment={row.original} />,
        enableSorting: false,
        enableHiding: false,
    };

    const localDocumentsColumn: ColumnDef<ShipmentItem> = {
        id: "documents",
        header: createStandardHeader("Documents"),
        cell: ({ row }) => <DocumentsCell shipment={row.original} showInvoice={false} />,
        enableSorting: false,
        enableHiding: false,
    };

    if (status === "delivered") {
        return [...baseColumns, deliveryDateColumn, cnDistancekmColumn, loadTypeColumn, documentsColumn];
    }

    if (status === "local" || status === "dmv") {
        return [...baseColumns, cnDistancekmColumn, localDocumentsColumn];
    }

    if (status === "in_stock") {
        return [...baseColumns, stockStatusColumn, stockLocationColumn, stockSinceColumn, stockDaysColumn];
    }

    return [
        ...baseColumns,
        eddColumn,
        cnDistancekmColumn,
        remainingColumn,
        loadTypeColumn,
        actionsColumn,
    ];
};
